import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, CheckCircle2, Award, Users, Truck as TruckIcon, Heart } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { FEATURES, ABOUT_IMAGE } from '../mock';
import { ReviewsCarousel } from '../components/ReviewsCarousel';
import LicensesSection from '../components/LicensesSection';

const stats = [
  { icon: TruckIcon, k: 'Own vans', v: 'From 1 man & van to a 7.5 tonne lorry' },
  { icon: Users, k: 'Homes & businesses', v: 'Moves, deliveries and clearances' },
  { icon: Award, k: 'Instant quotes', v: 'Online, any time' },
  { icon: Heart, k: 'Google reviews', v: 'See what customers say' },
];

const About = () => {
  return (
    <>
      <PageHeader
        crumb="About"
        title="About Move Van Ltd"
        subtitle="An independent, family-run removals and clearance company based in East London — serving London and the surrounding counties since 2014."
      />

      <section className="section">
        <div className="container-xl grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <img src={ABOUT_IMAGE} alt="Move Van team" className="rounded-xl w-full aspect-[4/3] object-cover shadow-xl" />
          </div>
          <div>
            <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">Our story</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--mv-navy)] leading-tight">Built on trust, driven by care.</h2>
            <p className="text-slate-700 mt-5 leading-relaxed">
              What started as a single van and a determined driver has grown into a removals and clearance team with <span className="font-bold text-[color:var(--mv-navy)]">18 vans in our own fleet</span>. We cover Greater London and the towns around it — from Reading, Slough and Guildford to Watford, Luton, St Albans, Chelmsford, Oxford, Cambridge, Milton Keynes and Brighton.
            </p>
            <p className="text-slate-700 mt-4 leading-relaxed">
              We know moving can be stressful — that’s why we’ve made it our mission to deliver a service that feels anything but. We take care with your belongings and keep you updated on the day.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/#quote" className="btn-primary">Work with us <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/services" className="btn-ghost-dark">Our services</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-[color:var(--mv-navy)] text-white">
        <div className="container-xl grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          {stats.map((s) => (
            <div key={s.v} className="text-center">
              <s.icon className="w-8 h-8 text-[color:var(--mv-orange-2)] mx-auto" />
              <div className="font-display text-2xl sm:text-4xl md:text-5xl font-extrabold mt-3 break-words hyphens-auto">{s.k}</div>
              <div className="text-white/70 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="container-xl">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">Our values</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--mv-navy)]">The way we work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => {
              const Icon = Icons[f.icon] || CheckCircle2;
              return (
                <div key={f.title} className="rounded-xl p-7 border border-slate-100 hover:-translate-y-1 hover:shadow-lg transition-all bg-white">
                  <div className="w-12 h-12 rounded-md bg-orange-50 text-[color:var(--mv-orange)] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)] mt-4">{f.title}</h3>
                  <p className="text-slate-600 mt-2 leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <LicensesSection />

      <ReviewsCarousel />
    </>
  );
};

export default About;
